import React from 'react';

// ---------------------------------------------------------------------------
// Numbered pagination: « ‹ 1 2 3 … N › »
//
// Shows the first page, the last page, and a small window around the current
// page, with "…" for any gap — standard behaviour for catalogs with hundreds
// or thousands of pages (our admin inventory can have 2,000+ pages at 50/page,
// and the storefront search can have 5,000+ at 24/page — rendering every page
// number would itself be a performance/layout problem).
//
// Every button has a fixed min-width so the bar's total width — and therefore
// its centered position — doesn't change between pages. (The other half of
// fixing the "pagination shifts sideways" issue is the global scrollbar-gutter
// fix in index.css; this component is the other half.)
// ---------------------------------------------------------------------------

const buildPageWindow = (page, totalPages, siblingCount = 1) => {
  // page is 0-indexed here; work in 1-indexed for display, convert at the end.
  const current = page + 1;
  const total = Math.max(totalPages, 1);

  const start = Math.max(2, current - siblingCount);
  const end = Math.min(total - 1, current + siblingCount);

  const pages = [1];
  if (start > 2) pages.push('ellipsis-start');
  for (let p = start; p <= end; p++) pages.push(p);
  if (end < total - 1) pages.push('ellipsis-end');
  if (total > 1) pages.push(total);

  return pages;
};

const Pagination = ({ page, totalPages, onPageChange, size = 'md' }) => {
  if (totalPages <= 1) return null;

  const items = buildPageWindow(page, totalPages);
  const btnSize = size === 'sm' ? 30 : 34;

  const baseBtnStyle = {
    minWidth: btnSize, height: btnSize, padding: '0 8px',
    borderRadius: 8, fontSize: size === 'sm' ? 12 : 13, fontWeight: 600,
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    border: '1px solid #e5e7eb', background: '#fff', color: '#374151',
    cursor: 'pointer', fontFamily: 'DM Sans, sans-serif',
    transition: 'all .15s',
  };

  const activeBtnStyle = {
    ...baseBtnStyle,
    background: '#4f46e5', color: '#fff', borderColor: '#4f46e5',
  };

  const disabledBtnStyle = {
    ...baseBtnStyle,
    opacity: 0.4, cursor: 'not-allowed',
  };

  return (
    <nav
      aria-label="Pagination"
      style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexWrap: 'wrap', gap: 6, maxWidth: '100%',
      }}
    >
      <button
        aria-label="First page"
        disabled={page === 0}
        onClick={() => onPageChange(0)}
        style={page === 0 ? disabledBtnStyle : baseBtnStyle}
      >
        «
      </button>
      <button
        aria-label="Previous page"
        disabled={page === 0}
        onClick={() => onPageChange(Math.max(0, page - 1))}
        style={page === 0 ? disabledBtnStyle : baseBtnStyle}
      >
        ‹ Prev
      </button>

      {items.map((item, i) =>
        item === 'ellipsis-start' || item === 'ellipsis-end' ? (
          <span key={`${item}-${i}`} style={{ padding: '0 4px', color: '#9ca3af', fontSize: 13 }}>…</span>
        ) : (
          <button
            key={item}
            aria-label={`Page ${item}`}
            aria-current={item === page + 1 ? 'page' : undefined}
            onClick={() => onPageChange(item - 1)}
            style={item === page + 1 ? activeBtnStyle : baseBtnStyle}
          >
            {item}
          </button>
        )
      )}

      <button
        aria-label="Next page"
        disabled={page >= totalPages - 1}
        onClick={() => onPageChange(Math.min(totalPages - 1, page + 1))}
        style={page >= totalPages - 1 ? disabledBtnStyle : baseBtnStyle}
      >
        Next ›
      </button>
      <button
        aria-label="Last page"
        disabled={page >= totalPages - 1}
        onClick={() => onPageChange(totalPages - 1)}
        style={page >= totalPages - 1 ? disabledBtnStyle : baseBtnStyle}
      >
        »
      </button>
    </nav>
  );
};

export default Pagination;